// ══════════════════════════════════════════════════════════
// CONFIG — Constants & pure utilities (no dependencies)
// ══════════════════════════════════════════════════════════
export const APP_VERSION    = '2.11.2';
export const SLOT_H         = 20;
export const START_H        = 6;
export const END_H          = 23;
export const TOTAL_S        = (END_H - START_H) * 4;  // 68 slots
export const MIN_DUR        = 1;  // minimum 1 slot = 15 min
export const TOAST_MS       = 2200;
export const FOCUS_DELAY_MS = 50;
export const ERROR_RESET_MS = 1500;

export const DAYS_SHORT = ['Dl','Dm','Dc','Dj','Dv','Ds','Dg'];
export const DAYS_LONG  = ['Dilluns','Dimarts','Dimecres','Dijous','Divendres','Dissabte','Diumenge'];
export const MONTHS     = ['gener','febrer','març','abril','maig','juny','juliol','agost','setembre','octubre','novembre','desembre'];
export const DAY_PILL_LABELS = ['L','M','X','J','V','S','D'];

export const GCAL_COLORS = {
  '1': '#7986cb', '2': '#33b679', '3': '#8e24aa', '4': '#e67c73',
  '5': '#f6bf26', '6': '#f4511e', '7': '#039be5', '8': '#616161',
  '9': '#3f51b5', '10': '#0b8043', '11': '#d50000',
};

export const COLORS = [
  { id:'sky'     },
  { id:'violet'  },
  { id:'rose'    },
  { id:'amber'   },
  { id:'emerald' },
  { id:'coral'   },
  { id:'indigo'  },
  { id:'lime'    },
  { id:'orange'  },
  { id:'teal'    },
];

export const ENERGIA_LIST = [
  { id:'alta',   label:'Alta',   dot:'var(--energy-high)', hex:'#DC2626' },
  { id:'mitjana',label:'Mitjana',dot:'var(--energy-med)',  hex:'#D97706' },
  { id:'baixa',  label:'Baixa',  dot:'var(--energy-low)',  hex:'#059669' },
];

// ── ICE prioritization (Impact × Confidence × Ease, escala 1–3) ──
// Each factor: id (col name), label, short letter, hint (one-liner shown next to the label),
// and `values`: per-button description shown in the dashed caption under the buttons.
export const ICE_FACTORS = [
  {
    id: 'ice_impact', label: 'Impacte', short: 'I',
    hint: 'què canvia quan ho fas',
    values: {
      1: 'Cas puntual · es nota poc',
      2: 'Ús ocasional · millora notable',
      3: 'Ús constant · estalvi recurrent',
    },
  },
  {
    id: 'ice_confidence', label: 'Confiança', short: 'C',
    hint: 'saps que funcionarà',
    values: {
      1: 'Incert · pot no funcionar',
      2: 'Probable · ho he fet abans',
      3: 'Segur · sé fer-ho i funciona',
    },
  },
  {
    id: 'ice_ease', label: 'Facilitat', short: 'F',
    hint: 'quant de feina costa',
    values: {
      1: 'Dies · o depèn de tercers',
      2: 'Hores · una tarda',
      3: 'Ràpid · menys d\'una hora',
    },
  },
];

// Pure: ICE score = I × C × F. Range 1–27, or null if any factor is missing / out of [1,3].
// INT columns arrive as numbers from Postgres.js, but coerce defensively.
export function iceScore(t) {
  const i = Number(t.ice_impact), c = Number(t.ice_confidence), e = Number(t.ice_ease);
  if (![i, c, e].every(v => Number.isFinite(v) && v >= 1 && v <= 3)) return null;
  return i * c * e;
}

// Tiers: 1–7 baixa, 8–17 mitjana, 18–27 alta.
export const ICE_TIERS = [
  { id: 'baixa',   label: 'baixa',   desc: 'al fons o descartar', max: 8 },        // score < 8
  { id: 'mitjana', label: 'mitjana', desc: 'cua normal',          max: 18 },       // 8 ≤ score < 18
  { id: 'alta',    label: 'alta',    desc: 'fer ja',              max: Infinity }, // score ≥ 18
];
export function iceTier(score) {
  if (score == null || !Number.isFinite(score)) return null;
  return ICE_TIERS.find(t => score < t.max);
}

// Posició (0–100%) sobre una barra de 3 zones iguals (baixa/mitjana/alta).
export function iceBarPercent(score) {
  if (score == null || !Number.isFinite(score)) return null;
  if (score < 8)  return ((score - 1) / 7)  * (100 / 3);
  if (score < 18) return (100 / 3) + ((score - 8) / 10) * (100 / 3);
  return (200 / 3) + Math.min(1, (score - 18) / 9) * (100 / 3);
}

// ── Pure utility functions ──────────────────────────────
export function getMonday(d) {
  const dd = new Date(d);
  dd.setHours(0,0,0,0);
  const day = dd.getDay();
  dd.setDate(dd.getDate() + (day === 0 ? -6 : 1 - day));
  return dd;
}

export function weekKey(mon) {
  const y = mon.getFullYear();
  const m = String(mon.getMonth() + 1).padStart(2, '0');
  const d = String(mon.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function slotToHHMM(slot) {
  const min = START_H * 60 + slot * 15;
  return `${String(Math.floor(min/60)).padStart(2,'0')}:${String(min%60).padStart(2,'0')}`;
}

// Format "DD/MM/YYYY HH:mm" en hora local. Buit si la data no és vàlida.
export function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// Durada llegible a partir de mil·lisegons: "1h 30m" / "45m" / "0m".
// Arrodoneix a minuts; negatius es tracten com 0.
export function formatDuration(ms) {
  const totalMin = Math.max(0, Math.round((Number(ms) || 0) / 60000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// Minuts en format curt: "4h", "1h 30m", "45m". null/buit → ''.
export function formatMinutesShort(min) {
  if (min == null || min === '' || isNaN(Number(min))) return '';
  const total = Math.max(0, Math.round(Number(min)));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h > 0) return m ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
}

// Llegeix una durada escrita a mà i la retorna en minuts.
// "4h" → 240, "1h30" / "1h 30m" → 90, "45m" / "45" → 45, "1,5h" → 90.
// Buit → null (sense estimació); text que no s'entén → NaN.
export function parseDurationInput(str) {
  const v = String(str ?? '').trim().toLowerCase().replace(',', '.');
  if (!v) return null;
  if (/^\d+(\.\d+)?$/.test(v)) return Math.round(Number(v));
  const m = v.match(/^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m?)?$/);
  if (!m || (m[1] == null && m[2] == null)) return NaN;
  return Math.round(Number(m[1] || 0) * 60 + Number(m[2] || 0));
}

// Data límit relativa a avui: { text: "d'aquí 3 dies", overdue: false }.
// dateStr és "YYYY-MM-DD" (o ISO); retorna null si no hi ha data vàlida.
export function relativeDeadline(dateStr, today = new Date()) {
  if (!dateStr) return null;
  const [y, mo, d] = String(dateStr).slice(0, 10).split('-').map(Number);
  if (!y || !mo || !d) return null;
  const due = Date.UTC(y, mo - 1, d);
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const diff = Math.round((due - now) / 86400000);
  if (diff === 0)  return { text: 'avui', overdue: false };
  if (diff === 1)  return { text: 'demà', overdue: false };
  if (diff === -1) return { text: 'ahir', overdue: true };
  return diff > 0
    ? { text: `d'aquí ${diff} dies`, overdue: false }
    : { text: `fa ${-diff} dies`, overdue: true };
}

// Data curta en hora local: "Dm 3 nov".
export function formatShortDate(d) {
  const dt = d instanceof Date ? d : new Date(d);
  return `${DAYS_SHORT[(dt.getDay() + 6) % 7]} ${dt.getDate()} ${MONTHS[dt.getMonth()].slice(0, 3)}`;
}

// Cronòmetre en viu "HH:MM:SS" a partir de mil·lisegons. Negatius → 0.
export function formatHMS(ms) {
  const totalSec = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const p = n => String(n).padStart(2, '0');
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${p(h)}:${p(m)}:${p(s)}`;
}

export function genId() {
  return Math.random().toString(36).slice(2,9) + Date.now().toString(36);
}

export function dateISO(monday, dayIndex) {
  const d = new Date(monday);
  d.setDate(d.getDate() + dayIndex);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

export function esc(str) {
  const d = document.createElement('div');
  d.appendChild(document.createTextNode(str));
  return d.innerHTML;
}

export function toast(msg) {
  const el = document.getElementById('toast');
  el.textContent = msg;
  el.classList.add('show');
  clearTimeout(el._t);
  el._t = setTimeout(() => el.classList.remove('show'), TOAST_MS);
}

export function snakeToCamel(b) {
  return {
    id: b.id, title: b.title, day: b.day,
    startSlot: b.start_slot ?? b.startSlot,
    dur: b.dur, color: b.color, notes: b.notes,
    tipusId: b.tipus_id ?? b.tipusId ?? null,
    energia: b.energia, templateId: b.template_id ?? b.templateId ?? null,
    done: b.done, doneAt: b.done_at ?? b.doneAt ?? null,
    calendarEventId: b.calendar_event_id ?? b.calendarEventId ?? null,
    taskIds: b.task_ids ?? b.taskIds ?? [],
  };
}

// Vista del calendari: només 'week' o 'day'; qualsevol altra cosa → 'week'.
export const CAL_VIEW_KEY = 'tb.calView';
export function sanitizeCalView(value) {
  return value === 'day' ? 'day' : 'week';
}

// ── Color utilities (pure) ───────────────────────────────────
// Parse "#rgb" or "#rrggbb" → {r,g,b} in 0–255. Returns null if invalid.
export function hexToRgb(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r)      h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else                h = (r - g) / d + 4;
    h /= 6;
  }
  return { h, s, l };
}

function hslToRgb({ h, s, l }) {
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const to255 = x => Math.round(x * 255);
  return { r: to255(r), g: to255(g), b: to255(b) };
}

function rgbToHex({ r, g, b }) {
  const c = x => x.toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

// Calendar color → text color forced to a uniform lightness, so light and
// dark hues read with similar visual weight (transparency option "C").
export function hexToUniformDarkText(hex, lightness = 0.35) {
  const rgb = hexToRgb(hex) || hexToRgb('#4285f4');
  const hsl = rgbToHsl(rgb);
  hsl.l = lightness;
  return rgbToHex(hslToRgb(hsl));
}
